import { useQuery } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { RefreshCw, Phone, Mail, CheckCircle, Activity, History, AlertTriangle } from "lucide-react"
import { api } from "@/lib/api"
import { cn } from "@/lib/utils"

type AutomationKpi = {
  key: string
  label: string
  today: number
  week: number
  description?: string | null
}
type AutomationKpisResponse = {
  kpis: AutomationKpi[]
  generated_at: string
}

/**
 * Dashboard — Kunden-Übersicht.
 *
 * 1:1-Port von admin/dashboard.py::admin_dashboard (der Hauptseite, nicht
 * der Detail-View). Zeigt Karten pro Kunde mit:
 *   - Name + Pulse-Dot wenn Dringlichkeit
 *   - Anzahl Tenants
 *   - 3 Mini-Stats: Calls (rot) / Mails (amber) / Tasks (indigo)
 *
 * Klick auf Karte → Detail-Dashboard (kommt in Phase 4/5, nicht jetzt).
 * Aktuell: Klick geht auf /app/ zurück (No-Op) damit keine 404.
 */
export default function Dashboard() {
  const {
    data, isLoading, error, isFetching, refetch,
  } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/dashboard", {})
      if (error) throw new Error(String(error))
      return data
    },
  })

  if (isLoading) {
    return <div className="p-10 text-muted-foreground">Lädt Kunden-Übersicht...</div>
  }
  if (error || !data) {
    return (
      <div className="p-10 text-destructive">
        Fehler: {error instanceof Error ? error.message : "unbekannt"}
      </div>
    )
  }

  const now = new Date().toLocaleString("de-DE", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  })

  return (
    <>
      {/* Top-Stand-Bar mit Auto-Refresh + Push Toggles — 1:1 wie altes Portal */}
      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 10, marginBottom: 16, fontSize: 12, color: "var(--text-muted)", flexWrap: "wrap",
        // Rechts liegen die festen Knoepfe (Lupe usw.) — ohne Abstand lag „Push" darunter (#96)
        paddingRight: "var(--kopf-frei)" }}>
        <span>🕐 Stand: <strong style={{ color: "var(--text)" }}>{now}</strong></span>
        <AutoRefreshToggle onRefresh={() => refetch()} />
        <PushToggle />
      </div>

      <AutomationKpiBar />
      <ActivityKpiBar />

      <div className="pp-page-header">
        <h2>Kunden</h2>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="pp-btn pp-btn-sm"
        >
          <RefreshCw className={cn("w-3 h-3", isFetching && "animate-spin")} />
          Aktualisieren
        </button>
      </div>

      {/* Kunden-Grid */}
      {data.kunden.length === 0 ? (
        <p className="text-center py-12 text-muted-foreground">
          Keine Kunden konfiguriert
        </p>
      ) : (
        <div className="ks-grid">
          {data.kunden.map((k) => (
            <KundeCard key={k.kunden_slug} kunde={k} />
          ))}
        </div>
      )}

      {/* Sync-Footer */}
      <div className="text-xs text-muted-foreground mt-10 space-y-0.5">
        {data.sync.last_email_sync && (
          <div>Letzter E-Mail-Sync: {fmtTime(data.sync.last_email_sync)}</div>
        )}
        {data.sync.last_sipgate_sync && (
          <div>Letzter Sipgate-Call: {fmtTime(data.sync.last_sipgate_sync)}</div>
        )}
      </div>
    </>
  )
}

/**
 * Einzelne Kunden-Karte — entspricht .ks-card aus admin/dashboard.py.
 */
function KundeCard({
  kunde,
}: {
  kunde: {
    kunden_slug: string
    kunden_name: string
    tenant_count: number
    open_calls: number
    open_emails: number
    open_tasks: number
    urgent: boolean
  }
}) {
  return (
    <Link to={`/kunde/${kunde.kunden_slug}`} className="ks-card">
      <div className="ks-name">
        {kunde.kunden_name}
        {kunde.urgent && (
          <span
            className="portal-pulse inline-block w-2 h-2 rounded-full"
            style={{ background: "var(--danger-color)", marginLeft: 8 }}
            aria-label="Dringend"
          />
        )}
      </div>
      <div className="ks-tenants">
        {kunde.tenant_count} Tenant{kunde.tenant_count !== 1 ? "s" : ""}
      </div>
      <div className="ks-stats">
        <span className="ks-stat ks-red"><Phone className="w-3 h-3" /> {kunde.open_calls}</span>
        <span className="ks-stat ks-amber"><Mail className="w-3 h-3" /> {kunde.open_emails}</span>
        <span className="ks-stat ks-indigo"><CheckCircle className="w-3 h-3" /> {kunde.open_tasks}</span>
      </div>
    </Link>
  )
}

/**
 * localStorage abgesichert — genau wie in `hooks/useListDensity.ts`.
 *
 * Der Zugriff kann werfen (Privatmodus verweigert ihn, unter jsdom gibt es
 * ein kaputtes globales `localStorage`). Ein Wurf im useState-Initializer
 * reisst die ganze Seite ab, deshalb hier nie ungeschuetzt lesen/schreiben.
 */
function lsGet(key: string): string | null {
  try { return localStorage.getItem(key) } catch { return null }
}
function lsSet(key: string, value: string): void {
  try { localStorage.setItem(key, value) } catch { /* Privatmodus */ }
}

/** Auto-Refresh-Toggle: alle 60s automatisch refetch. */
function AutoRefreshToggle({ onRefresh }: { onRefresh: () => void }) {
  const [enabled, setEnabled] = useState(() => lsGet("auto_refresh") === "1")
  useEffect(() => {
    if (!enabled) return
    const iv = setInterval(onRefresh, 60000)
    return () => clearInterval(iv)
  }, [enabled, onRefresh])
  const toggle = () => {
    const next = !enabled
    setEnabled(next)
    lsSet("auto_refresh", next ? "1" : "0")
  }
  return (
    <button
      onClick={toggle}
      style={{
        fontSize: 12, padding: "3px 10px", borderRadius: 6,
        border: "1px solid var(--border-color)",
        background: enabled ? "rgba(16,185,129,0.1)" : "var(--bg-card)",
        color: enabled ? "#065f46" : "var(--text-muted)",
        cursor: "pointer", fontFamily: "inherit",
      }}
    >
      Auto-Refresh: {enabled ? "An" : "Aus"}
    </button>
  )
}

/** Push-Toggle: Browser-Notifications an/aus. */
function PushToggle() {
  const [enabled, setEnabled] = useState(() => {
    if (typeof Notification === "undefined") return false
    return Notification.permission === "granted" && lsGet("push_on") === "1"
  })
  const toggle = async () => {
    if (!enabled) {
      if (typeof Notification === "undefined") { alert("Browser unterstützt keine Notifications"); return }
      if (Notification.permission !== "granted") {
        const perm = await Notification.requestPermission()
        if (perm !== "granted") return
      }
      setEnabled(true)
      lsSet("push_on", "1")
    } else {
      setEnabled(false)
      lsSet("push_on", "0")
    }
  }
  return (
    <button
      onClick={toggle}
      style={{
        fontSize: 12, padding: "3px 10px", borderRadius: 6,
        border: "1px solid var(--border-color)",
        background: enabled ? "rgba(245,158,11,0.1)" : "var(--bg-card)",
        color: enabled ? "#92400e" : "var(--text-muted)",
        cursor: "pointer", fontFamily: "inherit",
      }}
    >
      🔔 Push: {enabled ? "An" : "Aus"}
    </button>
  )
}

/**
 * Automation-KPIs — Sichtbarkeit fuer Background-Automatik.
 * Zeigt was die Plattform in den letzten 24h/7T automatisch erledigt hat.
 * Wenn ein Tile lange auf 0 steht, ist das ein Hinweis auf silent broken features.
 */
function AutomationKpiBar() {
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard", "automation"],
    queryFn: async (): Promise<AutomationKpisResponse> => {
      const resp = await fetch("/api/v1/dashboard/automation", { credentials: "include" })
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
      return resp.json()
    },
    refetchInterval: 60000,
  })

  if (isLoading || !data) {
    return (
      <div style={{ marginBottom: 18, fontSize: 12, color: "var(--text-muted)" }}>
        Lade Automatik-KPIs…
      </div>
    )
  }

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 6,
        fontSize: 12, fontWeight: 600, textTransform: "uppercase",
        letterSpacing: 0.5, color: "var(--text-muted)", marginBottom: 8,
      }}>
        <Activity className="w-3 h-3" />
        Automatik (24h / 7 Tage)
      </div>
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        gap: 10,
      }}>
        {data.kpis.map((k) => <KpiTile key={k.key} k={k} />)}
      </div>
    </div>
  )
}

// KPI-Key → Activity-Log Filter (source-Param). Ermoeglicht Drill-Down vom
// Dashboard auf die einzelnen Switch-Eintraege.
const KPI_LINK_TARGET: Record<string, string | null> = {
  doc_status_switches: "/aktivitaet?source=doc_status_switch",
  calendar_status_switches: "/aktivitaet?source=hero_polling",
  plans_executed: "/aktivitaet?source=review",
  autosave_hits: null,
  customer_confirmed_slots: null,
}

function KpiTile({ k }: { k: AutomationKpi }) {
  const stale = k.today === 0 && k.week === 0
  const target = KPI_LINK_TARGET[k.key] || null
  const inner = (
    <>
      <div style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.2 }}>
        {k.label}
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
        <span style={{
          fontSize: 22, fontWeight: 700,
          color: stale ? "var(--text-muted)" : "var(--text)",
        }}>
          {k.today}
        </span>
        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
          heute · {k.week} Woche
        </span>
      </div>
    </>
  )
  const baseStyle: React.CSSProperties = {
    padding: "10px 12px",
    borderRadius: 8,
    border: "1px solid var(--border-color)",
    background: stale ? "rgba(245,158,11,0.05)" : "var(--bg-card)",
    display: "flex", flexDirection: "column", gap: 4,
    textDecoration: "none", color: "inherit",
    cursor: target ? "pointer" : "default",
  }
  if (target) {
    return (
      <Link to={target} title={k.description || ""} style={baseStyle}>
        {inner}
      </Link>
    )
  }
  return <div title={k.description || ""} style={baseStyle}>{inner}</div>
}

type ActivitySummary = {
  actions_today: number
  actions_week: number
  errors_today: number
  errors_week: number
  unacked_errors: number
}

/**
 * Activity-KPI-Bar — Heute X Aktionen, Y Fehler. Klick fuehrt zur
 * /aktivitaet-Seite mit passendem Filter.
 */
function ActivityKpiBar() {
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard", "activity-summary"],
    queryFn: async (): Promise<ActivitySummary> => {
      const resp = await fetch("/api/v1/activity-log/summary", { credentials: "include" })
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
      return resp.json()
    },
    refetchInterval: 60000,
  })

  if (isLoading || !data) return null

  const errorTone = data.unacked_errors > 0
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 6,
        fontSize: 12, fontWeight: 600, textTransform: "uppercase",
        letterSpacing: 0.5, color: "var(--text-muted)", marginBottom: 8,
      }}>
        <History className="w-3 h-3" />
        Plattform-Aktivitaet
      </div>
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        gap: 10,
      }}>
        <Link
          to="/aktivitaet"
          style={{
            padding: "10px 12px",
            borderRadius: 8,
            border: "1px solid var(--border-color)",
            background: "var(--bg-card)",
            display: "flex", flexDirection: "column", gap: 4,
            textDecoration: "none", color: "inherit",
          }}
        >
          <div style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.2 }}>
            Aktionen
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
            <span style={{ fontSize: 22, fontWeight: 700 }}>{data.actions_today}</span>
            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
              heute · {data.actions_week} Woche
            </span>
          </div>
        </Link>

        <Link
          to="/aktivitaet?unacked=1"
          style={{
            padding: "10px 12px",
            borderRadius: 8,
            border: errorTone ? "1px solid rgba(239,68,68,0.45)" : "1px solid var(--border-color)",
            background: errorTone ? "rgba(239,68,68,0.06)" : "var(--bg-card)",
            display: "flex", flexDirection: "column", gap: 4,
            textDecoration: "none", color: "inherit",
          }}
        >
          <div style={{
            fontSize: 12, lineHeight: 1.2,
            color: errorTone ? "rgb(239,68,68)" : "var(--text-muted)",
            display: "flex", alignItems: "center", gap: 4,
          }}>
            <AlertTriangle className="w-3 h-3" />
            Fehler {errorTone ? "unerledigt" : ""}
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
            <span style={{
              fontSize: 22, fontWeight: 700,
              color: errorTone ? "rgb(220,38,38)" : "var(--text)",
            }}>
              {data.unacked_errors}
            </span>
            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
              {data.errors_today} heute · {data.errors_week} Woche
            </span>
          </div>
        </Link>
      </div>
    </div>
  )
}

function fmtTime(iso: string | null | undefined): string {
  if (!iso) return "nie"
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    })
  } catch {
    return iso.slice(0, 16)
  }
}
